import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';

const PORTAL_BASE_URL = process.env.REACT_APP_PORTAL_BASE_URL || 'http://localhost:8000';
const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${PORTAL_BASE_URL}/${path.replace(/^\/+/, '')}`;
};

function AlbumDetail() {
    const { id } = useParams();
    const [album, setAlbum] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [descExpanded, setDescExpanded] = useState(false);

    useEffect(() => {
        AOS.init({ duration: 800, once: true });

        const ALBUMS_API_URL = process.env.REACT_APP_PORTAL_BASE_URL
            ? `${process.env.REACT_APP_PORTAL_BASE_URL}/api/site/albums`
            : 'https://portal.petrochoice.ae/api/site/albums';

        axios
            .get(ALBUMS_API_URL)
            .then((res) => {
                const albums = res.data?.success ? (res.data.data || []) : (res.data || []);
                const found = albums.find((a) => String(a.id) === String(id));
                if (found) {
                    setAlbum(found);
                } else {
                    setError('Album not found');
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error('Error fetching album:', err.message);
                setError('Failed to load album');
                setLoading(false);
            });
    }, [id]);

    const openLightbox = (idx) => {
        setLightboxIndex(idx);
        setLightboxOpen(true);
        document.body.style.overflow = 'hidden';
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        document.body.style.overflow = '';
    };

    const goPrev = () => {
        if (!album?.images) return;
        setLightboxIndex((prev) => (prev - 1 + album.images.length) % album.images.length);
    };

    const goNext = () => {
        if (!album?.images) return;
        setLightboxIndex((prev) => (prev + 1) % album.images.length);
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!lightboxOpen) return;
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowLeft') goPrev();
            if (e.key === 'ArrowRight') goNext();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    });

    const DESC_MAX_LENGTH = 200;
    const description = album?.description || '';
    const isLongDesc = description.length > DESC_MAX_LENGTH;
    const displayDesc = descExpanded || !isLongDesc ? description : description.slice(0, DESC_MAX_LENGTH) + '...';

    if (loading) {
        return (
            <div style={styles.loaderWrap}>
                <div style={styles.spinner}></div>
                <p style={{ color: '#6b7280', marginTop: '16px' }}>Loading album...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.errorWrap}>
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#f77c00', marginBottom: '16px' }}></i>
                <h3 style={{ color: '#1e293b' }}>{error}</h3>
                <Link to="/albums" style={styles.backBtn}>
                    <i className="fas fa-arrow-left"></i> Back to Gallery
                </Link>
            </div>
        );
    }

    const images = album?.images || [];

    return (
        <div style={styles.page}>
            {/* Hero Section */}
            <div style={styles.heroSection} data-aos="fade-down">
                <div style={styles.heroOverlay}></div>
                <img
                    src={album.cover_image_url ? getImageUrl(album.cover_image_url) : '/assets/about us im 01.jpeg'}
                    alt={album.title}
                    style={styles.heroImage}
                />
                <div style={styles.heroContent}>
                    <Link to="/albums" style={styles.backLink}>
                        <i className="fas fa-arrow-left"></i> Back to Gallery
                    </Link>
                    <h1 style={styles.heroTitle}>{album.title}</h1>
                    <div style={styles.heroMeta}>
                        <span style={styles.photoCount}>
                            <i className="fas fa-images"></i> {images.length} Photos
                        </span>
                    </div>
                </div>
            </div>

            {/* Description Section */}
            {description && (
                <div style={styles.descSection} data-aos="fade-up">
                    <div style={styles.descCard}>
                        <div style={styles.descIcon}>
                            <i className="fas fa-quote-left" style={{ color: '#f77c00', fontSize: '24px' }}></i>
                        </div>
                        <p style={styles.descText}>{displayDesc}</p>
                        {isLongDesc && (
                            <button
                                onClick={() => setDescExpanded(!descExpanded)}
                                style={styles.readMoreBtn}
                            >
                                {descExpanded ? 'Show Less' : 'Read More'}
                                <i className={`fas fa-chevron-${descExpanded ? 'up' : 'down'}`} style={{ marginLeft: '6px', fontSize: '12px' }}></i>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Photo Grid */}
            <div style={styles.gridContainer}>
                {images.length === 0 ? (
                    <div style={styles.emptyState} data-aos="fade-up">
                        <i className="fas fa-camera-retro" style={{ fontSize: '64px', color: '#d1d5db', marginBottom: '16px' }}></i>
                        <h4 style={{ color: '#6b7280' }}>No photos in this album yet</h4>
                    </div>
                ) : (
                    <div style={styles.masonryGrid}>
                        {images.map((img, idx) => {
                            // Create varying sizes for visual interest
                            const sizeClass = idx % 7 === 0 ? 'large' : idx % 5 === 0 ? 'tall' : idx % 4 === 0 ? 'wide' : 'normal';
                            const gridStyle = {
                                ...styles.gridItem,
                                ...(sizeClass === 'large' ? styles.gridItemLarge : {}),
                                ...(sizeClass === 'tall' ? styles.gridItemTall : {}),
                                ...(sizeClass === 'wide' ? styles.gridItemWide : {}),
                            };

                            return (
                                <div
                                    key={img.id}
                                    style={gridStyle}
                                    data-aos="zoom-in"
                                    data-aos-delay={Math.min(idx * 50, 400)}
                                    onClick={() => openLightbox(idx)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'scale(1.02)';
                                        e.currentTarget.querySelector('.overlay').style.opacity = '1';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'scale(1)';
                                        e.currentTarget.querySelector('.overlay').style.opacity = '0';
                                    }}
                                >
                                    <img
                                        src={getImageUrl(img.url || img.image_path)}
                                        alt={img.caption || `Photo ${idx + 1}`}
                                        style={styles.gridImage}
                                        loading="lazy"
                                    />
                                    <div className="overlay" style={styles.imageOverlay}>
                                        <i className="fas fa-search-plus" style={{ fontSize: '28px', color: '#fff', marginBottom: '8px' }}></i>
                                        {img.caption && (
                                            <span style={styles.imageCaption}>{img.caption}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Lightbox */}
            {lightboxOpen && images.length > 0 && (
                <div style={styles.lightboxBackdrop} onClick={closeLightbox}>
                    <div style={styles.lightboxContent} onClick={(e) => e.stopPropagation()}>
                        {/* Close button */}
                        <button style={styles.lightboxClose} onClick={closeLightbox}>
                            <i className="fas fa-times"></i>
                        </button>

                        {/* Counter */}
                        <div style={styles.lightboxCounter}>
                            {lightboxIndex + 1} / {images.length}
                        </div>

                        {/* Previous */}
                        <button style={styles.lightboxPrev} onClick={goPrev}>
                            <i className="fas fa-chevron-left"></i>
                        </button>

                        {/* Image */}
                        <img
                            src={getImageUrl(images[lightboxIndex]?.url || images[lightboxIndex]?.image_path)}
                            alt={images[lightboxIndex]?.caption || `Photo ${lightboxIndex + 1}`}
                            style={styles.lightboxImage}
                        />

                        {/* Next */}
                        <button style={styles.lightboxNext} onClick={goNext}>
                            <i className="fas fa-chevron-right"></i>
                        </button>

                        {/* Caption */}
                        {images[lightboxIndex]?.caption && (
                            <div style={styles.lightboxCaption}>
                                {images[lightboxIndex].caption}
                            </div>
                        )}
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}

const styles = {
    page: {
        minHeight: '80vh',
        backgroundColor: '#f8fafc',
    },
    loaderWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
    },
    spinner: {
        width: '48px',
        height: '48px',
        border: '4px solid #e2e8f0',
        borderTopColor: '#f77c00',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    errorWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        padding: '40px',
        textAlign: 'center',
    },
    backBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 24px',
        backgroundColor: '#f77c00',
        color: '#fff',
        borderRadius: '25px',
        textDecoration: 'none',
        fontWeight: '600',
        marginTop: '16px',
        transition: 'all 0.3s ease',
    },
    // Hero Section
    heroSection: {
        position: 'relative',
        height: '400px',
        overflow: 'hidden',
    },
    heroOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.7) 100%)',
        zIndex: 2,
    },
    heroImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        position: 'absolute',
        top: 0,
        left: 0,
    },
    heroContent: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '40px',
        zIndex: 3,
        maxWidth: '1200px',
        margin: '0 auto',
    },
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        color: '#fff',
        textDecoration: 'none',
        fontSize: '14px',
        fontWeight: '500',
        marginBottom: '12px',
        opacity: 0.9,
        transition: 'opacity 0.3s ease',
    },
    heroTitle: {
        color: '#fff',
        fontSize: 'clamp(24px, 4vw, 40px)',
        fontWeight: '800',
        marginBottom: '12px',
        textShadow: '0 2px 10px rgba(0,0,0,0.3)',
    },
    heroMeta: {
        display: 'flex',
        gap: '20px',
        alignItems: 'center',
    },
    photoCount: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        color: '#fff',
        fontSize: '15px',
        fontWeight: '500',
        backgroundColor: 'rgba(247, 124, 0, 0.85)',
        padding: '6px 16px',
        borderRadius: '20px',
    },
    // Description Section
    descSection: {
        maxWidth: '900px',
        margin: '0 auto',
        padding: '32px 20px 0',
    },
    descCard: {
        background: '#fff',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        borderLeft: '4px solid #f77c00',
        position: 'relative',
    },
    descIcon: {
        marginBottom: '12px',
    },
    descText: {
        color: '#475569',
        fontSize: '16px',
        lineHeight: '1.8',
        margin: 0,
        whiteSpace: 'pre-line',
    },
    readMoreBtn: {
        background: 'none',
        border: 'none',
        color: '#f77c00',
        fontWeight: '600',
        cursor: 'pointer',
        fontSize: '14px',
        padding: '8px 0 0',
        display: 'inline-flex',
        alignItems: 'center',
        transition: 'color 0.3s ease',
    },
    // Grid
    gridContainer: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '32px 20px 60px',
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 20px',
    },
    masonryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '16px',
        gridAutoRows: '250px',
        gridAutoFlow: 'dense',
    },
    gridItem: {
        position: 'relative',
        borderRadius: '12px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    },
    gridItemLarge: {
        gridColumn: 'span 2',
        gridRow: 'span 2',
    },
    gridItemTall: {
        gridRow: 'span 2',
    },
    gridItemWide: {
        gridColumn: 'span 2',
    },
    gridImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
        transition: 'transform 0.5s ease',
    },
    imageOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 60%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        opacity: 0,
        transition: 'opacity 0.3s ease',
    },
    imageCaption: {
        color: '#fff',
        fontSize: '13px',
        textAlign: 'center',
        padding: '0 16px',
        maxWidth: '90%',
    },
    // Lightbox
    lightboxBackdrop: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'fadeInUp 0.3s ease',
    },
    lightboxContent: {
        position: 'relative',
        width: '90vw',
        height: '90vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    lightboxClose: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        background: 'rgba(255,255,255,0.15)',
        border: 'none',
        color: '#fff',
        fontSize: '24px',
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.3s ease',
        zIndex: 10,
        backdropFilter: 'blur(8px)',
    },
    lightboxCounter: {
        position: 'absolute',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        color: 'rgba(255,255,255,0.7)',
        fontSize: '14px',
        fontWeight: '500',
        zIndex: 10,
    },
    lightboxPrev: {
        position: 'absolute',
        left: '10px',
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'rgba(255,255,255,0.15)',
        border: 'none',
        color: '#fff',
        fontSize: '22px',
        width: '52px',
        height: '52px',
        borderRadius: '50%',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.3s ease',
        zIndex: 10,
        backdropFilter: 'blur(8px)',
    },
    lightboxNext: {
        position: 'absolute',
        right: '10px',
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'rgba(255,255,255,0.15)',
        border: 'none',
        color: '#fff',
        fontSize: '22px',
        width: '52px',
        height: '52px',
        borderRadius: '50%',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.3s ease',
        zIndex: 10,
        backdropFilter: 'blur(8px)',
    },
    lightboxImage: {
        maxWidth: '85%',
        maxHeight: '80vh',
        objectFit: 'contain',
        borderRadius: '8px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
    },
    lightboxCaption: {
        position: 'absolute',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        color: '#fff',
        fontSize: '15px',
        textAlign: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)',
        padding: '10px 24px',
        borderRadius: '25px',
        maxWidth: '80%',
        backdropFilter: 'blur(8px)',
        zIndex: 10,
    },
};

export default AlbumDetail;
